import { ChevronDown } from '@velocity-exchange/icons';
import TradeInput from 'src/components/Inputs/TradeInput';
import ToggleInput from 'src/components/Inputs/ToggleInput';
import { BigNum, isVariant, PositionDirection } from '@velocity-exchange/sdk';
import { MarketId, UIOrderType } from '@velocity-exchange/common';
import React, { useCallback, useEffect } from 'react';
import { cj } from 'src/utils/classNames';
import TpSlOrderFormEstimatedPnL from 'src/components/TradeForm/TpSlOrderForm/TpSlOrderFormEstimatedPnL';
import { TpSlTriggerPercentBasis } from 'src/components/TradeForm/TpSlOrderForm/types';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { DEFAULT_TP_SL_TRIGGER_PERCENT_BASIS } from './consts';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import useModal from 'src/components/Modals/hooks/useModal';
import TpSlOrderFormMessage from 'src/components/TradeForm/TpSlOrderForm/TpSlOrderFormMessage';
import SizeInput from 'src/components/TradeForm/SizeInput/SizeInput';
import { useTpSlOrderFormReducer } from 'src/hooks/useTpSlOrderFormReducer';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import Select from 'src/components/primitives/Select/Select';
import Slider from 'src/components/primitives/Slider/Slider';
import { useCancelOrders } from 'src/hooks/trade/cancel/useCancelOrders';

// Threshold for determining if position size has been manually edited
// Used to decide whether to auto-update size when entry size changes
const SIZE_EDIT_THRESHOLD = 0.0001;

/**
 * Normalizes two BigNum values to have the same precision to prevent comparison errors
 * in the calculatePotentialProfit function
 */
const normalizePrecisionToPrimary = (
	primary: BigNum,
	secondary: BigNum
): BigNum => {
	return secondary.shiftTo(primary.precision);
};

interface Props {
	isTakeProfit: boolean;
	baseOrderDirection: PositionDirection;
	triggerPrice: BigNum | null;
	triggerPriceStringValue: string;
	triggerPercentStringValue: string;
	triggerOrderDirection: PositionDirection;
	entrySize: BigNum;
	entryPrice: BigNum;
	selectedMarketId: MarketId | undefined;
	updatePrice: (
		price: string,
		isTakeProfit: boolean,
		orderType?: UIOrderType
	) => void;
	updatePercent: (
		percent: string,
		isTakeProfit: boolean,
		orderType?: UIOrderType
	) => void;
	updateLimitPrice?: (price: string, isTakeProfit: boolean) => void;
	limitPrice?: BigNum | null;
	limitPriceStringValue?: string;
	updateOrders: () => void;
	orderType: UIOrderType;
	setOrderType?: (orderType: UIOrderType) => void;
	disabled: boolean;
	orderId?: number;
	usePercentBasisDropdown?: boolean;
	// Message-related props
	formDisabled: boolean;
	invalid: boolean;
	invalidReason?: 'entry' | 'immediate' | 'reference' | null;
	positionIsLong: boolean;
	isStopLossBeyondLiquidationPrice?: boolean;
	// Size input props for individual TP/SL size control
	partialPositionSelected?: boolean;
	stepSize: number;
	baseAssetSymbol?: string;
	markPrice: BigNum;
	// Callback to report the calculated order size to parent
	onOrderSizeChange?: (isTakeProfit: boolean, size: BigNum) => void;
	// Saved order values for restoring prices when switching back to full position
	savedTriggerPrice?: BigNum | null | undefined;
	savedLimitPrice?: BigNum | null | undefined;
}

const TpSlOrderFormInputsV2 = React.memo(function TpSlOrderFormInputsV2({
	isTakeProfit,
	baseOrderDirection,
	triggerPrice,
	triggerPriceStringValue,
	triggerPercentStringValue,
	triggerOrderDirection,
	entrySize,
	entryPrice,
	selectedMarketId,
	updatePrice,
	updatePercent,
	updateLimitPrice,
	limitPrice,
	limitPriceStringValue,
	updateOrders,
	orderType,
	setOrderType,
	disabled,
	orderId,
	usePercentBasisDropdown,
	// Message-related props
	formDisabled,
	invalid,
	invalidReason,
	positionIsLong,
	isStopLossBeyondLiquidationPrice,
	// Size input props
	partialPositionSelected,
	stepSize,
	baseAssetSymbol,
	markPrice,
	onOrderSizeChange,
	// Saved order values
	savedTriggerPrice,
	savedLimitPrice,
}: Props) {
	const { show } = useModal();
	const { cancelOrders } = useCancelOrders();

	// Use the reducer hook directly
	const [reducerState, dispatch] = useTpSlOrderFormReducer(
		entrySize.toNum().toString(),
		stepSize,
		markPrice
	);

	const {
		userInputEntrySize,
		userInputEntrySizeString,
		userInputEntryQuoteSizeString,
		sliderPercentage,
		cancellingOrder,
	} = reducerState;

	// Sync when entrySize or markPrice changes - but only initialize, don't override user input
	useEffect(() => {
		// Only re-initialize when entrySize changes significantly or on first load
		if (markPrice && entrySize) {
			// Check if current size is close to entry size (suggesting it hasn't been manually edited)
			const currentSizeNum = userInputEntrySize?.toNum() || 0;
			const entrySizeNum = entrySize.toNum();
			const isCloseToEntrySize =
				Math.abs(currentSizeNum - entrySizeNum) < SIZE_EDIT_THRESHOLD;

			// Only update if the size hasn't been manually modified or if it's the first load
			if (isCloseToEntrySize || !userInputEntrySize) {
				dispatch({
					type: 'setSliderPercentage',
					value: { sliderPercentage: sliderPercentage || 100, markPrice },
				});
			}
		}
	}, [dispatch, entrySize, markPrice]);

	// Sync partialPositionSelected prop with reducer state
	useEffect(() => {
		if (partialPositionSelected !== undefined) {
			dispatch({
				type: 'setPartialPositionSelected',
				value: { partialPositionSelected },
			});
		}
	}, [dispatch, partialPositionSelected]);

	// Reset form fields to defaults when switching to partial form for existing orders
	useEffect(() => {
		if (partialPositionSelected && orderId && markPrice) {
			// Reset size inputs to 100% of position
			dispatch({
				type: 'setSliderPercentage',
				value: { sliderPercentage: 100, markPrice },
			});

			// Also reset the size inputs directly in the reducer
			dispatch({
				type: 'setUserInputEntryBaseSize',
				value: { size: entrySize.toNum().toString() },
			});

			updatePrice('', isTakeProfit, orderType);
			updateLimitPrice?.('', isTakeProfit);
			// Clear orderId in parent component - parent manages this state
			// Parent component should handle clearing orderId when switching to partial mode

			// Don't clear the price fields - keep the saved order prices
			// The modal handles price display through triggerPriceStringValue props
		}
	}, [dispatch, partialPositionSelected]);

	useEffect(() => {
		if (triggerPercentStringValue) {
			updatePercent(triggerPercentStringValue, isTakeProfit, orderType);
		}
	}, [orderType]);

	// Reset input size to order size when switching back to full position mode with existing order
	useEffect(() => {
		if (!partialPositionSelected && orderId && markPrice) {
			// Reset size inputs to the entry size (which represents the order size for existing orders)
			dispatch({
				type: 'setSliderPercentage',
				value: { sliderPercentage: 100, markPrice },
			});

			dispatch({
				type: 'setUserInputEntryBaseSize',
				value: { size: entrySize.toNum().toString() },
			});

			// Restore saved order prices when switching back to full position mode
			if (savedTriggerPrice) {
				updatePrice(
					savedTriggerPrice.toNum().toString(),
					isTakeProfit,
					orderType
				);
			}
			if (savedLimitPrice) {
				updateLimitPrice?.(savedLimitPrice.toNum().toString(), isTakeProfit);
			}
		}
	}, [partialPositionSelected]);

	// Report the calculated order size to parent when it changes
	useEffect(() => {
		if (onOrderSizeChange && userInputEntrySize) {
			onOrderSizeChange(isTakeProfit, userInputEntrySize);
		}
	}, [
		onOrderSizeChange,
		isTakeProfit,
		userInputEntrySize,
		sliderPercentage,
		partialPositionSelected,
	]);

	// Size change handlers - dispatch to reducer
	const handleBaseSizeChange = useCallback(
		(value: string) => {
			dispatch({
				type: 'setUserInputEntryBaseSize',
				value: { size: value },
			});
		},
		[dispatch]
	);

	const handleQuoteSizeChange = useCallback(
		(value: string) => {
			if (markPrice) {
				dispatch({
					type: 'setUserInputEntryQuoteSize',
					value: { size: value, markPrice },
				});
			}
		},
		[dispatch, markPrice]
	);

	const handleSliderValueUpdate = useCallback(
		(value: number) => {
			if (markPrice) {
				dispatch({
					type: 'setSliderPercentage',
					value: { sliderPercentage: value, markPrice },
				});
			}
		},
		[dispatch, markPrice]
	);

	const isMobileScreenSize = useIsMobileScreenSize();

	const [triggerPercentBasis, setTriggerPercentBasis] = useSyncLocalStorage(
		'tradeFormTpSlTriggerPercentBasis',
		DEFAULT_TP_SL_TRIGGER_PERCENT_BASIS
	);

	const showTriggerPercentBasisModal = () => {
		if (disabled) {
			return;
		}

		show({ type: 'tp_sl_trigger_percent_basis', props: {} });
	};

	const triggerPercentBasisOptions: {
		label: string;
		value: TpSlTriggerPercentBasis;
	}[] = [
		{ label: 'Price %', value: 'price' },
		{ label: 'ROI %', value: 'roi' },
	];

	const { percentBasisTriggerLabel, percentInputPlaceholder } = (() => {
		if (isMobileScreenSize && !usePercentBasisDropdown) {
			return {
				percentBasisTriggerLabel: '%',
				percentInputPlaceholder:
					triggerPercentBasis === 'price' ? 'Price' : 'ROI',
			};
		}

		return {
			percentBasisTriggerLabel:
				triggerPercentBasis === 'price' ? 'Price %' : 'ROI %',
			percentInputPlaceholder: undefined,
		};
	})();

	const changeTriggerPercentBasisButton = (
		<button
			className={cj(
				'flex items-center gap-x-px whitespace-nowrap',
				'typo-body-xs',
				disabled ? 'text-text-disabled' : 'text-text-secondary',
				isMobileScreenSize &&
					'border-l border-input-border pl-xs text-text-secondary'
			)}
			onClick={showTriggerPercentBasisModal}
		>
			{percentBasisTriggerLabel}
			<ChevronDown size={14} />
		</button>
	);

	const {
		open,
		onOpenChange,
		value,
		onValueChange,
		selectOptions,
		selectedOption,
	} = useSelect({
		options: triggerPercentBasisOptions,
		onSelectedValueChange: setTriggerPercentBasis,
		selectedValue: triggerPercentBasis,
	});

	const percentInputPrefix = (() => {
		if (usePercentBasisDropdown) {
			return (
				<Select.Root
					open={open}
					onOpenChange={onOpenChange}
					value={value}
					onValueChange={onValueChange}
				>
					<Select.Trigger
						className="h-auto border-0 p-0 hover:bg-transparent"
						valueClassName="typo-body-xs text-text-secondary hover:text-text-default gap-x-0"
					>
						{selectedOption?.label}
					</Select.Trigger>
					<Select.Options sideOffset={12}>
						{selectOptions.map((option) => (
							<Select.Option key={option.value} value={option.value}>
								{option.label}
							</Select.Option>
						))}
					</Select.Options>
				</Select.Root>
			);
		}

		if (!isMobileScreenSize) {
			return changeTriggerPercentBasisButton;
		}
	})();

	const percentInputSuffix = (() => {
		if (isMobileScreenSize && !usePercentBasisDropdown) {
			return changeTriggerPercentBasisButton;
		}

		if (usePercentBasisDropdown) {
			return undefined;
		}

		return (
			<span
				className={cj(
					'text-text-default transition-colors',
					disabled && 'text-text-disabled'
				)}
			>
				%
			</span>
		);
	})();

	useEffect(() => {
		updateOrders();
	}, [
		triggerPriceStringValue,
		triggerPercentStringValue,
		limitPriceStringValue,
		isTakeProfit,
		updateOrders,
	]);

	const handleUpdatePrice = useCallback(
		(newValue: string) => {
			updatePrice(newValue, isTakeProfit, orderType);
		},
		[updatePrice, isTakeProfit, orderType]
	);

	const handleUpdatePercent = useCallback(
		(newValue: string) => {
			updatePercent(newValue, isTakeProfit, orderType);
		},
		[updatePercent, isTakeProfit, orderType]
	);

	const handleUpdateLimitPrice = useCallback(
		(newValue: string) => {
			updateLimitPrice?.(newValue, isTakeProfit);
		},
		[updateLimitPrice, isTakeProfit]
	);

	const handleCloseOrder = useCallback(async () => {
		if (orderId) {
			dispatch({
				type: 'cancellingOrder',
				value: { cancellingOrder: isTakeProfit ? 'tp' : 'sl' },
			});

			try {
				const toastId = `tp-sl-form-v2-cancel-order-${orderId}-${Date.now()}`;
				await cancelOrders(toastId, orderId);

				// Only clear inputs and refresh data after successful cancellation
				updatePrice('', isTakeProfit, orderType);

				// Force refresh account data to ensure existing orders state is cleared
				const accountsState = useVelocityAccountStore.getState();
				const userAccount =
					accountsState.accounts[accountsState.currentUserKey ?? ''];
				if (userAccount?.client) {
					try {
						await userAccount.client.fetchAccounts();
					} catch (error) {
						console.warn(
							'Failed to refresh account data after order cancellation:',
							error
						);
					}
				}
			} catch (error) {
				// If cancellation fails (user rejects, etc.), don't clear the inputs
				console.warn('Order cancellation failed or was rejected:', error);
			} finally {
				dispatch({
					type: 'cancellingOrder',
					value: { cancellingOrder: undefined },
				});
			}
		}
	}, [cancelOrders, orderId, updatePrice, isTakeProfit, orderType]);

	return (
		<div className="flex flex-col gap-y-md">
			{/* Header */}
			<div className="flex items-center justify-between">
				<span className="typo-label-sm text-text-default">
					{isTakeProfit ? 'Take Profit' : 'Stop Loss'}
				</span>
				{/* Cancel Order Button */}
				{orderId && !partialPositionSelected && (
					<TextButton
						size="md"
						variant="tertiary"
						onClick={handleCloseOrder}
						isLoading={cancellingOrder === (isTakeProfit ? 'tp' : 'sl')}
					>
						Cancel Order
					</TextButton>
				)}
			</div>

			{/* Input Fields Row 1 */}
			<div className="flex flex-col gap-y-sm">
				<div className="grid grid-cols-[1fr_144px] gap-x-xs">
					<TradeInput
						id={isTakeProfit ? 'tp-price-input-v2' : 'sl-price-input-v2'}
						size="xs"
						labelVariant="inset"
						label={isTakeProfit ? 'TP' : 'SL'}
						onChange={handleUpdatePrice}
						onBlur={updateOrders}
						value={triggerPriceStringValue}
						disabled={disabled}
						dataTestId={
							isTakeProfit
								? 'tpsl-modal-tp-trigger-input'
								: 'tpsl-modal-sl-trigger-input'
						}
					/>
					<TradeInput
						id={isTakeProfit ? 'tp-percent-input-v2' : 'sl-percent-input-v2'}
						size="xs"
						labelVariant="none"
						onChange={handleUpdatePercent}
						onBlur={updateOrders}
						value={triggerPercentStringValue}
						prefix={percentInputPrefix}
						placeholder={percentInputPlaceholder}
						suffix={percentInputSuffix}
						disabled={disabled}
					/>
				</div>

				{/* Input Fields Row 2 */}
				<div className="grid grid-cols-[1fr_144px] gap-x-xs">
					<TradeInput
						id={isTakeProfit ? 'tp-usd-price-v2' : 'sl-usd-price-v2'}
						size="xs"
						labelVariant="inset"
						label="Price (USD)"
						value={orderType === 'market' ? '' : limitPriceStringValue}
						onChange={handleUpdateLimitPrice}
						onBlur={updateOrders}
						disabled={disabled || orderType === 'market'}
					/>
					<ToggleInput
						id={isTakeProfit ? 'tp-order-type-v2' : 'sl-order-type-v2'}
						size="xs"
						value={orderType}
						options={[
							{ value: 'market', label: 'Market' },
							{ value: 'limit', label: 'Limit' },
						]}
						onChange={(orderTypeValue) => {
							if (
								setOrderType &&
								(!orderId || partialPositionSelected) &&
								!disabled
							) {
								setOrderType(orderTypeValue);
							}
						}}
						disabled={disabled || (!!orderId && !partialPositionSelected)}
						valuesOnly
					/>
				</div>

				{/* Individual Size Control for each TP/SL order */}
				{/* Size Input Content */}
				{partialPositionSelected &&
					baseAssetSymbol &&
					userInputEntrySizeString !== undefined &&
					handleBaseSizeChange &&
					handleQuoteSizeChange &&
					markPrice &&
					stepSize !== undefined && (
						<>
							<SizeInput
								size="sm"
								baseSizeStringValue={userInputEntrySizeString}
								formattedMarketSymbol={baseAssetSymbol}
								quoteSizeStringValue={userInputEntryQuoteSizeString || ''}
								maxQuoteAvailable={entrySize.mul(markPrice).toNum()}
								onBaseSizeChange={handleBaseSizeChange}
								onQuoteSizeChange={handleQuoteSizeChange}
								stepSize={stepSize}
							/>
							{sliderPercentage !== undefined && handleSliderValueUpdate && (
								<div>
									<Slider
										id={`${
											isTakeProfit ? 'tp' : 'sl'
										}-order-form-partial-amount-slider-input`}
										value={sliderPercentage}
										onValueCommit={handleSliderValueUpdate}
										onValueChange={handleSliderValueUpdate}
									/>
								</div>
							)}
						</>
					)}

				{/* Estimated P&L */}
				<div className="flex w-full items-center justify-between typo-body-xs text-text-secondary">
					<span>Est. P&L:</span>
					{!invalid && selectedMarketId && (triggerPrice || limitPrice) ? (
						<TpSlOrderFormEstimatedPnL
							className="typo-body-xs"
							currentPositionSize={entrySize}
							currentPositionEntryPrice={entryPrice}
							currentPositionDirection={
								isVariant(baseOrderDirection, 'long') ? 'long' : 'short'
							}
							side={isVariant(triggerOrderDirection, 'long') ? 'buy' : 'sell'}
							limitExitSize={
								partialPositionSelected && userInputEntrySize
									? normalizePrecisionToPrimary(entrySize, userInputEntrySize)
									: entrySize
							}
							limitExitPrice={
								orderType === 'limit' && limitPrice
									? limitPrice
									: (triggerPrice ?? new BigNum(0))
							}
							isMarketOrder={orderType === 'market'}
						/>
					) : (
						<span>-</span>
					)}
				</div>
			</div>

			{/* TP/SL Form Message */}
			<TpSlOrderFormMessage
				formDisabled={formDisabled}
				takeProfitInvalid={isTakeProfit ? invalid : false}
				takeProfitInvalidReason={isTakeProfit ? invalidReason : null}
				stopLossInvalid={!isTakeProfit ? invalid : false}
				stopLossInvalidReason={!isTakeProfit ? invalidReason : null}
				positionIsLong={positionIsLong}
				isStopLossBeyondLiquidationPrice={
					!isTakeProfit ? (isStopLossBeyondLiquidationPrice ?? false) : false
				}
			/>
		</div>
	);
});

export default TpSlOrderFormInputsV2;
